import type { Money } from '@mybooks/ledger';
import { api, unwrap, type Schemas } from '../../api/client';
import { useApiQuery, type Account } from '../../api/hooks';
import { money } from '../../lib/format';
import { openBase } from '../../lib/fx';
import { fetchAging, fetchTb } from '../ledger/reports';
import type { BankAccount } from '../banking/data';

// Queries the dashboard widgets share. Keys match the rest of the app, so a widget and a page that
// ask for the same thing share one request, and a mutation's invalidate() refreshes the dashboard.

export type Doc = Schemas['Document'];
export type { BankAccount };

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** What is still open across documents, in the base currency (ADR 0029). */
export const balanceOf = (docs: readonly Doc[]) => docs.reduce<Money>((a, d) => a + openBase(d), 0n);
/** Overdue amount in an aging: every bucket past the due date. */
export const overdueOf = (t: Record<string, string>) => money(t.days_1_30) + money(t.days_31_60) + money(t.days_61_90) + money(t.over_90);

/** Invoices or bills in one status. The API pages at 200; a business past that needs a summary endpoint. */
async function listDocs(kind: 'invoices' | 'bills', query: { status?: 'open' | 'partial' | 'draft'; limit: number }): Promise<Doc[]> {
  const r = kind === 'invoices'
    ? await unwrap(api.GET('/v1/invoices', { params: { query } }))
    : await unwrap(api.GET('/v1/bills', { params: { query } }));
  return (r as { data?: Doc[] }).data ?? [];
}

export const useBankList = (enabled = true) => useApiQuery(['bank', 'accounts'], async () => ((await unwrap(api.GET('/v1/bank-accounts'))) as unknown as { data?: BankAccount[] }).data ?? [], { enabled });
export const useUnpaidInvoices = () => useApiQuery(['invoices', 'dashboard', 'unpaid'], async () => [...(await listDocs('invoices', { status: 'open', limit: 200 })), ...(await listDocs('invoices', { status: 'partial', limit: 200 }))]);
export const useUnpaidBills = () => useApiQuery(['bills', 'dashboard', 'unpaid'], async () => [...(await listDocs('bills', { status: 'open', limit: 200 })), ...(await listDocs('bills', { status: 'partial', limit: 200 }))]);
export const useRecentInvoices = () => useApiQuery(['invoices', 'dashboard', 'recent'], () => listDocs('invoices', { limit: 20 }));
export const useReceivablesAging = (asOf: string) => useApiQuery(['reports', 'aging', 'receivable', asOf], () => fetchAging('receivable', asOf));
export const useTrialBalance = (asOf: string) => useApiQuery(['reports', 'tb', asOf], () => fetchTb(asOf));

/**
 * The accounts money actually sits in: bank-type assets. The bank-accounts endpoint lists bank
 * accounts and credit cards (and any account with statements); credit cards are not cash.
 */
export function cashAccounts(bank: readonly BankAccount[], accounts: readonly Account[]): BankAccount[] {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  return bank.filter((b) => {
    const a = byId.get(b.id);
    if (!a || a.type !== 'asset') return false;
    if (a.system_key === 'bank' || b.kind || Number(b.line_count ?? 0) > 0) return true;
    return a.system_key === null && a.code >= '1000' && a.code < '1050';
  });
}
